<template>
  <div class="news-bg min-vh-100 py-5 px-2 px-md-0">
    <div class="container" style="max-width: 960px;">

      <!-- ── Title ── -->
      <div class="mb-5 animate__animated animate__fadeInDown">
        <h2 class="fw-bold mb-1" style="color: rgb(0, 75, 107);">
          <i class="fas fa-bullhorn me-2"></i> Anunțuri & Noutăți
        </h2>
        <div class="title-underline"></div>
      </div>

      <!-- ── Loading ── -->
      <div v-if="loading" class="text-center my-5 py-5">
        <div class="spinner-ring"></div>
        <div class="text-muted mt-3 small">Se încarcă anunțurile...</div>
      </div>

      <!-- ── Empty ── -->
      <div v-else-if="paginatedNews.length === 0" class="empty-state text-center py-5 animate__animated animate__fadeIn">
        <i class="fas fa-inbox fa-3x mb-3 d-block" style="color: rgb(0, 75, 107); opacity: 0.3;"></i>
        <div class="fw-semibold text-muted">Nu există anunțuri publicate momentan.</div>
      </div>

      <!-- ── Cards ── -->
      <div v-else class="row g-4">
        <div
          v-for="(post, index) in paginatedNews"
          :key="post.id"
          class="col-12 col-md-6 animate__animated animate__fadeInUp"
          :style="{ animationDelay: `${index * 0.1}s` }"
        >
          <div class="news-card h-100 rounded-4 shadow-sm overflow-hidden">

            <!-- Imagine -->
            <div class="news-img-wrap">
              <img
                v-if="post.image && post.image.length > 4"
                :src="getImageUrl(post.image)"
                :alt="post.title"
                class="news-img"
                @error="onImgError($event)"
              />
              <div v-else class="news-img-placeholder">
                <i class="fas fa-image fa-2x opacity-25"></i>
              </div>
            </div>

            <!-- Body -->
            <div class="card-body p-3 p-md-4">
              <div class="d-flex align-items-start justify-content-between gap-2 mb-2">
                <h5 class="fw-bold mb-0 news-title">{{ post.title }}</h5>
                <span class="news-date flex-shrink-0">
                  <i class="fas fa-calendar-alt me-1"></i>{{ formatDate(post.created_at) }}
                </span>
              </div>
              <div class="news-content text-muted" v-html="post.content"></div>
            </div>

            <!-- Fișiere atașate -->
            <div v-if="post.documents && post.documents.length" class="card-footer-custom px-3 px-md-4 pb-3 pt-3">
              <div class="files-label">
                <i class="fas fa-paperclip me-1"></i> Fișiere atașate
              </div>
              <div class="d-flex flex-column gap-1 mt-2">
                <a
                  v-for="doc in post.documents"
                  :key="doc.file"
                  :href="doc.file"
                  target="_blank"
                  class="file-link"
                >
                  <i class="fas fa-download me-2"></i>{{ doc.title }}
                </a>
              </div>
            </div>

          </div>
        </div>
      </div>

      <!-- ── Pagination ── -->
      <nav v-if="totalPages > 1" class="mt-5 d-flex justify-content-center gap-1">
        <button
          class="page-btn"
          :disabled="currentPage === 1"
          @click="changePage(currentPage - 1)"
        >
          <i class="fas fa-chevron-left fa-sm"></i>
        </button>
        <button
          v-for="page in totalPages"
          :key="page"
          class="page-btn"
          :class="{ 'page-btn--active': currentPage === page }"
          @click="changePage(page)"
        >
          {{ page }}
        </button>
        <button
          class="page-btn"
          :disabled="currentPage === totalPages"
          @click="changePage(currentPage + 1)"
        >
          <i class="fas fa-chevron-right fa-sm"></i>
        </button>
      </nav>

    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import api from '@/api/axios'

const news = ref([])
const loading = ref(true)
const currentPage = ref(1)
const perPage = 4

const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString('ro-RO', {
  year: 'numeric', month: 'long', day: 'numeric'
})

function getImageUrl(imagePath) {
  if (!imagePath || imagePath.length < 5) return null
  // Dacă URL-ul e deja absolut (http/https), îl returnăm ca atare
  if (imagePath.startsWith('http')) return imagePath
  const baseURL = api.defaults.baseURL.replace(/\/api\/?$/, '')
  // Evităm dublu slash
  const cleanPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`
  return `${baseURL}${cleanPath}`
}

function onImgError(event) {
  // La eroare, ascundem img și afișăm placeholder
  event.target.style.display = 'none'
  const wrap = event.target.closest('.news-img-wrap')
  if (wrap) {
    const placeholder = document.createElement('div')
    placeholder.className = 'news-img-placeholder'
    placeholder.innerHTML = '<i class="fas fa-image fa-2x opacity-25"></i>'
    wrap.appendChild(placeholder)
  }
}

const totalPages = computed(() => Math.ceil(news.value.length / perPage))

const paginatedNews = computed(() => {
  const start = (currentPage.value - 1) * perPage
  return news.value.slice(start, start + perPage)
})

function changePage(page) {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }
}

onMounted(async () => {
  try {
    const res = await api.get('/news/')
    news.value = res.data
  } catch (err) {
    console.error('Eroare la încărcarea știrilor:', err)
  } finally {
    loading.value = false
  }
})
</script>

<style scoped>
/* ── Layout ── */
.news-bg {
  background: #f8f9fa;
}

.title-underline {
  width: 48px;
  height: 3px;
  background: rgb(0, 75, 107);
  border-radius: 2px;
  margin-top: 0.5rem;
}

/* ── Spinner ── */
.spinner-ring {
  width: 44px;
  height: 44px;
  border: 3px solid #e9ecef;
  border-top-color: rgb(0, 75, 107);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin: 0 auto;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* ── Card ── */
.news-card {
  background: white;
  border: 1px solid rgba(0,0,0,0.06);
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  display: flex;
  flex-direction: column;
}
.news-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 28px rgba(0, 75, 107, 0.12) !important;
}

/* ── Imagine ── */
.news-img-wrap {
  width: 100%;
  height: 200px;
  overflow: hidden;
  background: #f1f5f9;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}
.news-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.4s ease;
}
.news-card:hover .news-img {
  transform: scale(1.03);
}
.news-img-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #e8eef3, #f1f5f9);
  color: rgb(0, 75, 107);
}

/* ── Body ── */
.news-title {
  font-size: 1rem;
  line-height: 1.4;
  color: #1a1a2e;
}
.news-date {
  font-size: 0.73rem;
  color: rgb(0, 75, 107);
  background: rgba(0, 75, 107, 0.08);
  padding: 0.2rem 0.55rem;
  border-radius: 2rem;
  white-space: nowrap;
}
.news-content {
  font-size: 0.88rem;
  line-height: 1.6;
  margin-top: 0.5rem;
}
.news-content :deep(p) {
  margin-bottom: 0.4rem;
}

/* ── Fișiere ── */
.card-footer-custom {
  border-top: 1px solid #f0f0f0;
  margin-top: auto;
}
.files-label {
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgb(0, 75, 107);
}
.file-link {
  display: inline-flex;
  align-items: center;
  font-size: 0.85rem;
  color: rgb(0, 75, 107);
  text-decoration: none;
  padding: 0.3rem 0.7rem;
  border: 1px solid rgba(0, 75, 107, 0.2);
  border-radius: 0.4rem;
  background: rgba(0, 75, 107, 0.04);
  transition: all 0.2s;
  width: fit-content;
}
.file-link:hover {
  background: rgb(0, 75, 107);
  color: white;
}

/* ── Pagination ── */
.page-btn {
  min-width: 38px;
  height: 38px;
  padding: 0 0.6rem;
  border: 1.5px solid rgba(0, 75, 107, 0.25);
  border-radius: 0.5rem;
  background: white;
  color: rgb(0, 75, 107);
  font-size: 0.88rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.page-btn:hover:not(:disabled) {
  background: rgb(0, 75, 107);
  color: white;
  border-color: rgb(0, 75, 107);
}
.page-btn--active {
  background: rgb(0, 75, 107) !important;
  color: white !important;
  border-color: rgb(0, 75, 107) !important;
}
.page-btn:disabled {
  opacity: 0.35;
  cursor: default;
}

/* ── Mobile ── */
@media (max-width: 576px) {
  .news-img-wrap {
    height: 160px;
  }
  .news-title {
    font-size: 0.95rem;
  }
  .news-date {
    font-size: 0.68rem;
  }
}
</style>